import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Badge,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import LockResetIcon from '@mui/icons-material/LockReset';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import FastForwardIcon from '@mui/icons-material/FastForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UpdateIcon from '@mui/icons-material/Update';
import InfoIcon from '@mui/icons-material/Info';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import BadgeIcon from '@mui/icons-material/Badge';
import { getApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

function UserManagement() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  
  // Admin verification
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Metrics
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  
  // Tab management
  const [currentTab, setCurrentTab] = useState(0);
  
  // Pending challenges
  const [pendingChallenges, setPendingChallenges] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  // Users
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Active challenges
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [loadingActive, setLoadingActive] = useState(false);
  
  // Admin users
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  
  // Challenge management states
  const [allChallengesWithProgress, setAllChallengesWithProgress] = useState([]);
  const [loadingChallengeProgress, setLoadingChallengeProgress] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState('All');
  const [scalpingViolations, setScalpingViolations] = useState([]);
  const [showScalpingDialog, setShowScalpingDialog] = useState(false);
  const [showAdvancePhaseDialog, setShowAdvancePhaseDialog] = useState(false);
  const [showFailChallengeDialog, setShowFailChallengeDialog] = useState(false);
  const [showResetChallengeDialog, setShowResetChallengeDialog] = useState(false);
  const [failureReason, setFailureReason] = useState('');
  const [scalpingReviewNotes, setScalpingReviewNotes] = useState('');
  const [selectedScalpingOrder, setSelectedScalpingOrder] = useState(null);
  
  // Dialog states
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showUserStatusDialog, setShowUserStatusDialog] = useState(false);
  const [showAdminPasswordDialog, setShowAdminPasswordDialog] = useState(false);
  const [showAssignChallengeDialog, setShowAssignChallengeDialog] = useState(false);
  const [showImpersonateDialog, setShowImpersonateDialog] = useState(false);
  const [showUserDetailsDialog, setShowUserDetailsDialog] = useState(false);
  
  // Selected items
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Free challenge assignment states
  const [freeChallengeType, setFreeChallengeType] = useState('5K');
  const [freeChallengeCustomName, setFreeChallengeCustomName] = useState('');
  const [freeChallengeNotes, setFreeChallengeNotes] = useState('');
  
  // Affiliate program states
  const [affiliates, setAffiliates] = useState([]);
  const [loadingAffiliates, setLoadingAffiliates] = useState(false);
  const [showAddAffiliateDialog, setShowAddAffiliateDialog] = useState(false);
  const [affiliateEmail, setAffiliateEmail] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateNotes, setAffiliateNotes] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [showAffiliateDetailsDialog, setShowAffiliateDetailsDialog] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateUsers, setAffiliateUsers] = useState([]);
  
  // Processing states
  const [processing, setProcessing] = useState(false);
  
  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // User Trades states (Feature 2)
  const [userTrades, setUserTrades] = useState([]);
  const [tradesSummary, setTradesSummary] = useState(null);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [selectedTradeUser, setSelectedTradeUser] = useState('');
  const [tradesStatusFilter, setTradesStatusFilter] = useState('ALL');
  const [tradesChallengeFilter, setTradesChallengeFilter] = useState('ALL');
  const [allUserEmails, setAllUserEmails] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Full user objects with name and email
  const [userSearchQuery, setUserSearchQuery] = useState(''); // Search filter

  // Platform filter state
  const [platformFilter, setPlatformFilter] = useState('ALL');
  const [activeChallengesPlatformFilter, setActiveChallengesPlatformFilter] = useState('ALL');

  // MT5 Manual Data Entry states
  const [showMT5DataDialog, setShowMT5DataDialog] = useState(false);
  const [mt5Data, setMT5Data] = useState({
    initialEquity: '',
    currentEquity: '',
    profit: '',
    ordersPlaced: '',
    dailyDrawdown: '',
    totalDrawdown: '',
    hasViolations: 'No',
    violationReason: ''
  });
  const [mt5SelectedUser, setMT5SelectedUser] = useState(null);
  const [mt5UserChallenges, setMT5UserChallenges] = useState([]); // User's MT5 challenges for dropdown
  const [selectedChallengeId, setSelectedChallengeId] = useState(''); // Selected challenge
  const [mt5Username, setMT5Username] = useState(''); // MT5 username
  const [mt5Password, setMT5Password] = useState(''); // MT5 password

  // MT5 Data History states
  const [showMT5HistoryDialog, setShowMT5HistoryDialog] = useState(false);
  const [mt5History, setMT5History] = useState([]);
  const [loadingMT5History, setLoadingMT5History] = useState(false);
  const [mt5HistoryUser, setMT5HistoryUser] = useState(null);

  // Verify admin access on mount
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const response = await fetch(getApiUrl('/api/admin/verify'), {
          method: 'GET',
          headers: getAuthHeaders()
        });

        const data = await response.json();
        
        if (response.status === 401 || response.status === 403) {
          logout();
          navigate('/signin');
          return;
        }

        if (data.success && data.isAdmin) {
          setIsAdmin(true);
          fetchMetrics();
          fetchPendingChallenges();
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error verifying admin:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    // Check localStorage directly first (for cases where AuthContext hasn't loaded yet)
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      verifyAdmin();
    } else if (user?.email) {
      verifyAdmin();
    } else {
      navigate('/signin');
    }
  }, [user, navigate]);

  // Feature 2: Refetch trades when filters change
  useEffect(() => {
    if (selectedTradeUser && currentTab === 5) {
      fetchUserTrades(selectedTradeUser);
    }
  }, [tradesStatusFilter, tradesChallengeFilter]);

  // Fetch affiliates when Affiliates tab is opened
  useEffect(() => {
    if (currentTab === 6) {
      fetchAffiliates();
    }
  }, [currentTab]);

  // Fetch metrics
  const fetchMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/metrics'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setMetrics(data.metrics);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Fetch pending challenges
  const fetchPendingChallenges = async () => {
    setLoadingPending(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/pending'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setPendingChallenges(data.challenges || []);
      }
    } catch (error) {
      console.error('Error fetching pending challenges:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/users'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch active challenges
  const fetchActiveChallenges = async () => {
    setLoadingActive(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/active'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setActiveChallenges(data.challenges || []);
      }
    } catch (error) {
      console.error('Error fetching active challenges:', error);
    } finally {
      setLoadingActive(false);
    }
  };

  const fetchAdminUsers = async () => {
    setLoadingAdmins(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/list'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setAdminUsers(data.admins || []);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Feature 2: Fetch all user emails for dropdown
  const fetchAllUserEmails = async () => {
    try {
      const response = await fetch(getApiUrl('/api/admin/users'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success && data.users) {
        // Store full user objects with FirstName, LastName, EmailID
        const usersWithDetails = data.users
          .filter(u => u.EmailID)
          .map(u => ({
            email: u.EmailID,
            firstName: u.FirstName || '',
            lastName: u.LastName || '',
            fullName: `${u.FirstName || ''} ${u.LastName || ''}`.trim()
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        
        setAllUsers(usersWithDetails);
        
        // Keep backwards compatibility
        const emails = usersWithDetails.map(u => u.email);
        setAllUserEmails(emails);
      }
    } catch (error) {
      console.error('Error fetching user emails:', error);
    }
  };

  // Feature 2: Fetch user trades
  const fetchUserTrades = async (userEmail) => {
    if (!userEmail) {
      setUserTrades([]);
      setTradesSummary(null);
      return;
    }

    setLoadingTrades(true);
    try {
      const params = new URLSearchParams({
        userEmail,
        limit: 50,
        status: tradesStatusFilter,
      });

      if (tradesChallengeFilter !== 'ALL') {
        params.append('challengeId', tradesChallengeFilter);
      }

      const response = await fetch(getApiUrl(`/api/admin/user-trades?${params}`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setUserTrades(data.trades || []);
        setTradesSummary(data.summary || null);
      } else {
        setErrorMessage(data.message || 'Failed to fetch user trades');
        setUserTrades([]);
        setTradesSummary(null);
      }
    } catch (error) {
      console.error('Error fetching user trades:', error);
      setErrorMessage('Failed to fetch user trades');
      setUserTrades([]);
      setTradesSummary(null);
    } finally {
      setLoadingTrades(false);
    }
  };

  // Fetch all affiliates
  const fetchAffiliates = async () => {
    setLoadingAffiliates(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/affiliates/list'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setAffiliates(data.affiliates || []);
      } else {
        setErrorMessage(data.error || 'Failed to fetch affiliates');
        setAffiliates([]);
      }
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      setErrorMessage('Failed to fetch affiliates');
      setAffiliates([]);
    } finally {
      setLoadingAffiliates(false);
    }
  };

  // Generate referral code
  const handleGenerateCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/affiliates/generate-code'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setAffiliateCode(data.code);
      } else {
        setErrorMessage(data.error || 'Failed to generate code');
      }
    } catch (error) {
      console.error('Error generating code:', error);
      setErrorMessage('Failed to generate referral code');
    } finally {
      setGeneratingCode(false);
    }
  };

  // Create new affiliate
  const handleCreateAffiliate = async () => {
    if (!affiliateEmail || !affiliateCode) {
      setErrorMessage('Email and referral code are required');
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/admin/affiliates/create'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: affiliateEmail,
          referralCode: affiliateCode,
          notes: affiliateNotes
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Affiliate created successfully');
        setShowAddAffiliateDialog(false);
        setAffiliateEmail('');
        setAffiliateCode('');
        setAffiliateNotes('');
        fetchAffiliates(); // Refresh list
      } else {
        setErrorMessage(data.error || 'Failed to create affiliate');
      }
    } catch (error) {
      console.error('Error creating affiliate:', error);
      setErrorMessage('Failed to create affiliate');
    }
  };

  // Extend affiliate expiry date
  const handleExtendExpiry = async (affiliateId, months = 3) => {
    try {
      const response = await fetch(getApiUrl('/api/admin/affiliates/extend-expiry'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ affiliateId, months })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(data.message);
        fetchAffiliates(); // Refresh list
        
        // If affiliate details dialog is open, update selected affiliate
        if (selectedAffiliate && selectedAffiliate.affiliateId === affiliateId) {
          const updatedAffiliate = { ...selectedAffiliate, expiryDate: data.newExpiryDate, isExpired: false };
          setSelectedAffiliate(updatedAffiliate);
        }
      } else {
        setErrorMessage(data.error || 'Failed to extend expiry date');
      }
    } catch (error) {
      console.error('Error extending expiry:', error);
      setErrorMessage('Failed to extend expiry date');
    }
  };

  // Fetch all challenges with progress for admin management
  const fetchAllChallengesWithProgress = async () => {
    setLoadingChallengeProgress(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/all-with-progress'), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setAllChallengesWithProgress(data.challenges || []);
      }
    } catch (error) {
      console.error('Error fetching challenges with progress:', error);
      setErrorMessage('Failed to load challenge data');
    } finally {
      setLoadingChallengeProgress(false);
    }
  };

  // Fetch scalping violations for a challenge
  const fetchScalpingViolations = async (challengeId) => {
    try {
      const response = await fetch(getApiUrl(`/api/challenges/${challengeId}/scalping-violations`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setScalpingViolations(data.violations || []);
        setShowScalpingDialog(true);
      }
    } catch (error) {
      console.error('Error fetching scalping violations:', error);
      setErrorMessage('Failed to load scalping violations');
    }
  };

  // Admin: Advance phase
  const handleAdvancePhase = async () => {
    setProcessing(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/advance-phase'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ challengeId: selectedChallenge.ChallengeID })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(data.message || 'Phase advanced successfully');
        fetchAllChallengesWithProgress();
        setShowAdvancePhaseDialog(false);
        setSelectedChallenge(null);
      } else {
        setErrorMessage(data.error || 'Failed to advance phase');
      }
    } catch (error) {
      console.error('Error advancing phase:', error);
      setErrorMessage('Failed to advance phase');
    } finally {
      setProcessing(false);
    }
  };

  // Admin: Fail challenge
  const handleFailChallenge = async () => {
    if (!failureReason.trim()) {
      setErrorMessage('Failure reason is required');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/fail'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          challengeId: selectedChallenge.ChallengeID,
          reason: failureReason
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Challenge failed successfully');
        fetchAllChallengesWithProgress();
        setShowFailChallengeDialog(false);
        setSelectedChallenge(null);
        setFailureReason('');
      } else {
        setErrorMessage(data.error || 'Failed to fail challenge');
      }
    } catch (error) {
      console.error('Error failing challenge:', error);
      setErrorMessage('Failed to fail challenge');
    } finally {
      setProcessing(false);
    }
  };

  // Admin: Reset challenge
  const handleResetChallenge = async () => {
    setProcessing(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/reset'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ challengeId: selectedChallenge.ChallengeID })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Challenge reset to Phase 1 successfully');
        fetchAllChallengesWithProgress();
        setShowResetChallengeDialog(false);
        setSelectedChallenge(null);
      } else {
        setErrorMessage(data.error || 'Failed to reset challenge');
      }
    } catch (error) {
      console.error('Error resetting challenge:', error);
      setErrorMessage('Failed to reset challenge');
    } finally {
      setProcessing(false);
    }
  };

  // Admin: Review scalping violation
  const handleReviewScalping = async (orderId, reviewed) => {
    setProcessing(true);
    try {
      const response = await fetch(getApiUrl('/api/admin/scalping/review'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          orderId,
          reviewed,
          notes: scalpingReviewNotes
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Scalping violation reviewed');
        // Refresh violations list
        if (selectedChallenge) {
          fetchScalpingViolations(selectedChallenge.ChallengeID);
        }
        setScalpingReviewNotes('');
        setSelectedScalpingOrder(null);
      } else {
        setErrorMessage(data.error || 'Failed to review violation');
      }
    } catch (error) {
      console.error('Error reviewing scalping:', error);
      setErrorMessage('Failed to review violation');
    } finally {
      setProcessing(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSuccessMessage('');
    setErrorMessage('');
    
    if (newValue === 1 && users.length === 0) {
      fetchUsers();
    } else if (newValue === 2 && activeChallenges.length === 0) {
      fetchActiveChallenges();
    } else if (newValue === 3 && adminUsers.length === 0) {
      fetchAdminUsers();
    } else if (newValue === 4 && allChallengesWithProgress.length === 0) {
      fetchAllChallengesWithProgress();
    } else if (newValue === 5 && allUserEmails.length === 0) {
      // Feature 2: Load user emails for dropdown
      fetchAllUserEmails();
    }
  };

  // Activate challenge
  const handleActivateChallenge = async () => {
    if (!selectedChallenge) return;

    setProcessing(true);
    setShowActivateDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/activate'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ challengeId: selectedChallenge.ChallengeID })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Challenge activated successfully for ${selectedChallenge.UserEmail}`);
        await fetchMetrics();
        await fetchPendingChallenges();
      } else {
        setErrorMessage(data.error || 'Failed to activate challenge');
      }
    } catch (error) {
      console.error('Error activating challenge:', error);
      setErrorMessage('Error activating challenge');
    } finally {
      setProcessing(false);
      setSelectedChallenge(null);
    }
  };

  // Reject pending challenge
  const handleRejectChallenge = async () => {
    if (!selectedChallenge) return;

    setProcessing(true);
    setShowRejectDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/reject'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          challengeId: selectedChallenge.ChallengeID,
          reason: rejectionReason || 'Rejected by admin'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Challenge rejected for ${selectedChallenge.UserEmail}`);
        await fetchMetrics();
        await fetchPendingChallenges();
      } else {
        setErrorMessage(data.error || 'Failed to reject challenge');
      }
    } catch (error) {
      console.error('Error rejecting challenge:', error);
      setErrorMessage('Error rejecting challenge');
    } finally {
      setProcessing(false);
      setSelectedChallenge(null);
      setRejectionReason('');
    }
  };

  // Deactivate challenge
  const handleDeactivateChallenge = async () => {
    if (!selectedChallenge) return;

    setProcessing(true);
    setShowDeactivateDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/challenges/deactivate'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ challengeId: selectedChallenge.ChallengeID })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Challenge deactivated successfully`);
        await fetchMetrics();
        await fetchActiveChallenges();
      } else {
        setErrorMessage(data.error || 'Failed to deactivate challenge');
      }
    } catch (error) {
      console.error('Error deactivating challenge:', error);
      setErrorMessage('Error deactivating challenge');
    } finally {
      setProcessing(false);
      setSelectedChallenge(null);
    }
  };

  // Toggle user status
  const handleToggleUserStatus = async () => {
    if (!selectedUser) return;

    const newStatus = !selectedUser.IsActive;
    setProcessing(true);
    setShowUserStatusDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/users/toggle-status'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId: selectedUser.CustomerID, isActive: newStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
        await fetchUsers();
        await fetchMetrics();
      } else {
        setErrorMessage(data.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setErrorMessage('Error updating user status');
    } finally {
      setProcessing(false);
      setSelectedUser(null);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    setProcessing(true);
    setShowPasswordDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/users/reset-password'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ userId: selectedUser.CustomerID, newPassword })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Password reset successfully for ${selectedUser.EmailID}`);
      } else {
        setErrorMessage(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setErrorMessage('Error resetting password');
    } finally {
      setProcessing(false);
      setSelectedUser(null);
      setNewPassword('');
    }
  };

  // Reset admin password
  const handleResetAdminPassword = async () => {
    if (!selectedAdmin || !newAdminPassword) return;

    setProcessing(true);
    setShowAdminPasswordDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/reset-admin-password'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ adminId: selectedAdmin.AdminID, newPassword: newAdminPassword })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Password reset successfully for admin ${selectedAdmin.EmailID}`);
      } else {
        setErrorMessage(data.error || 'Failed to reset admin password');
      }
    } catch (error) {
      console.error('Error resetting admin password:', error);
      setErrorMessage('Error resetting admin password');
    } finally {
      setProcessing(false);
      setSelectedAdmin(null);
      setNewAdminPassword('');
    }
  };

  // Assign free challenge to user
  const handleAssignFreeChallenge = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setShowAssignChallengeDialog(false);

    try {
      const response = await fetch(getApiUrl('/api/admin/assign-free-challenge'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userEmail: selectedUser.EmailID,
          challengeType: freeChallengeType,
          customName: freeChallengeCustomName || `Free ${freeChallengeType} Challenge`,
          notes: freeChallengeNotes || 'Assigned by admin'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`Free ${freeChallengeType} challenge assigned to ${selectedUser.FirstName} ${selectedUser.LastName}`);
        await fetchMetrics();
        await fetchUsers();
      } else {
        setErrorMessage(data.error || 'Failed to assign challenge');
      }
    } catch (error) {
      console.error('Error assigning challenge:', error);
      setErrorMessage('Error assigning challenge');
    } finally {
      setProcessing(false);
      setSelectedUser(null);
      setFreeChallengeType('5K');
      setFreeChallengeCustomName('');
      setFreeChallengeNotes('');
    }
  };

  // Handle MT5 Data Entry Submission
  const handleSubmitMT5Data = async () => {
    if (!mt5SelectedUser) return;

    setProcessing(true);

    try {
      const response = await fetch(getApiUrl('/api/admin/mt5-data-entry'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          userEmail: mt5SelectedUser.EmailID,
          initialEquity: mt5Data.initialEquity,
          currentEquity: mt5Data.currentEquity,
          profit: mt5Data.profit,
          ordersPlaced: mt5Data.ordersPlaced,
          dailyDrawdown: mt5Data.dailyDrawdown,
          totalDrawdown: mt5Data.totalDrawdown,
          hasViolations: mt5Data.hasViolations,
          violationReason: mt5Data.violationReason,
          challengeId: selectedChallengeId || undefined,
          mt5Username: mt5Username || undefined,
          mt5Password: mt5Password || undefined
        })
      });

      const data = await response.json();
      
      if (data.success) {
        const message = data.credentialsUpdated 
          ? `MT5 data and credentials submitted successfully for ${mt5SelectedUser.FirstName} ${mt5SelectedUser.LastName}. Profit Target: ${data.profitTargetPercent}%`
          : `MT5 data submitted successfully for ${mt5SelectedUser.FirstName} ${mt5SelectedUser.LastName}. Profit Target: ${data.profitTargetPercent}%`;
        setSuccessMessage(message);
        setShowMT5DataDialog(false);
        setMT5SelectedUser(null);
        setMT5Data({
          initialEquity: '',
          currentEquity: '',
          profit: '',
          ordersPlaced: '',
          dailyDrawdown: '',
          totalDrawdown: '',
          hasViolations: 'No',
          violationReason: ''
        });
        setSelectedChallengeId('');
        setMT5Username('');
        setMT5Password('');
        setMT5UserChallenges([]);
      } else {
        setErrorMessage(data.error || 'Failed to submit MT5 data');
      }
    } catch (error) {
      console.error('Error submitting MT5 data:', error);
      setErrorMessage('Error submitting MT5 data');
    } finally {
      setProcessing(false);
    }
  };

  // Fetch user's MT5 challenges for dropdown
  const fetchUserMT5Challenges = async (userEmail) => {
    try {
      const response = await fetch(getApiUrl(`/api/admin/user-mt5-challenges?userEmail=${encodeURIComponent(userEmail)}`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        setMT5UserChallenges(data.challenges || []);
        // Auto-select if only one challenge
        if (data.challenges && data.challenges.length === 1) {
          const challenge = data.challenges[0];
          setSelectedChallengeId(challenge.ChallengeID);
          setMT5Username(challenge.MT5Username || '');
          setMT5Password(challenge.MT5Password || '');
        }
      } else {
        console.error('Failed to fetch MT5 challenges:', data.error);
      }
    } catch (error) {
      console.error('Error fetching user MT5 challenges:', error);
    }
  };

  // Fetch MT5 Data History for a user
  const fetchMT5History = async (userEmail) => {
    setLoadingMT5History(true);
    console.log('🔍 Fetching MT5 history for:', userEmail);
    try {
      const response = await fetch(getApiUrl(`/api/admin/mt5-data-history?userEmail=${encodeURIComponent(userEmail)}`), {
        method: 'GET',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      console.log('📊 MT5 History Response:', data);
      
      if (data.success) {
        console.log('✅ MT5 History entries:', data.history?.length || 0);
        setMT5History(data.history || []);
      } else {
        console.error('❌ Error fetching MT5 history:', data.error);
        setErrorMessage(data.error || 'Failed to fetch MT5 data history');
        setMT5History([]);
      }
    } catch (error) {
      console.error('❌ Error fetching MT5 history:', error);
      setErrorMessage('Error fetching MT5 data history');
      setMT5History([]);
    } finally {
      setLoadingMT5History(false);
    }
  };

  // Handle impersonate user
  const handleImpersonateUser = async () => {
    if (!selectedUser) return;

    setProcessing(true);
    setShowImpersonateDialog(false);

    try {
      console.log('🔍 Impersonating user:', selectedUser.EmailID);
      const response = await fetch(getApiUrl('/api/admin/impersonate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          userEmail: selectedUser.EmailID
        })
      });

      console.log('📥 Response status:', response.status);
      const data = await response.json();
      console.log('📥 Response data:', data);

      if (response.ok && data.success) {
        setSuccessMessage(`Now viewing as ${selectedUser.FirstName} ${selectedUser.LastName}`);
        
        // Store the original admin token AND user data before replacing them
        const originalToken = localStorage.getItem('authToken');
        const originalUser = localStorage.getItem('user');
        
        localStorage.setItem('originalAdminToken', originalToken);
        localStorage.setItem('originalAdminUser', originalUser);
        
        // Store the impersonation token (using authToken to match AuthContext)
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify({
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          userType: 'customer'
        }));
        localStorage.setItem('impersonationActive', 'true');
        localStorage.setItem('impersonatedUser', JSON.stringify(data.user));
        localStorage.setItem('impersonatedBy', data.impersonation.impersonatedBy);
        
        // Redirect to user dashboard
        setTimeout(() => {
          window.location.href = '/trader-summary';
        }, 500);
      } else {
        console.error('❌ Impersonation failed:', data.error);
        setErrorMessage(data.error || 'Failed to impersonate user');
      }
    } catch (error) {
      console.error('❌ Error impersonating user:', error);
      setErrorMessage(`Error impersonating user: ${error.message}`);
    } finally {
      setProcessing(false);
      setSelectedUser(null);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Metric Card Component
  const MetricCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ 
      bgcolor: '#1e222d', 
      height: '100%',
      border: '1px solid #2a2e39',
      transition: 'all 0.3s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${color}33`
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            bgcolor: `${color}22`, 
            p: 1.5, 
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
            {value !== undefined ? value.toLocaleString() : '-'}
          </Typography>
        </Box>
        <Typography sx={{ color: '#d1d4dc', fontSize: 14, fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: '#888', fontSize: 12 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#131722'
      }}>
        <CircularProgress sx={{ color: '#4ade80' }} />
      </Box>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#131722',
      p: { xs: 2, md: 4 }
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
          Admin Dashboard
        </Typography>
        <Typography sx={{ color: '#d1d4dc', fontSize: 15 }}>
          Manage users, challenges, and monitor platform metrics
        </Typography>
      </Box>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert 
          severity="success" 
          onClose={() => setSuccessMessage('')}
          sx={{ mb: 3, bgcolor: 'rgba(74, 222, 128, 0.1)', color: '#4ade80', border: '1px solid #4ade80' }}
        >
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert 
          severity="error" 
          onClose={() => setErrorMessage('')}
          sx={{ mb: 3, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444' }}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Metrics Dashboard */}
      {metrics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <MetricCard
              title="Total Users"
              value={metrics.totalUsers}
              icon={<PeopleIcon sx={{ fontSize: 32, color: '#3b82f6' }} />}
              color="#3b82f6"
              subtitle="Registered customers"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <MetricCard
              title="Active Traders"
              value={metrics.activeTraders}
              icon={<TrendingUpIcon sx={{ fontSize: 32, color: '#4ade80' }} />}
              color="#4ade80"
              subtitle="With active challenges"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <MetricCard
              title="Pending Approval"
              value={metrics.challenges.pending}
              icon={<AssignmentIcon sx={{ fontSize: 32, color: '#fbbf24' }} />}
              color="#fbbf24"
              subtitle="Awaiting activation"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <MetricCard
              title="New Signups"
              value={metrics.recentSignups}
              icon={<PersonAddIcon sx={{ fontSize: 32, color: '#a855f7' }} />}
              color="#a855f7"
              subtitle="Last 30 days"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <MetricCard
              title="Total Revenue"
              value={`$${metrics.totalRevenue}`}
              icon={<AttachMoneyIcon sx={{ fontSize: 32, color: '#10b981' }} />}
              color="#10b981"
              subtitle="From active challenges"
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card sx={{ bgcolor: '#1e222d', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: '#888',
              fontWeight: 600,
              fontSize: 15,
              textTransform: 'none',
              minHeight: 60
            },
            '& .Mui-selected': {
              color: '#4ade80'
            },
            '& .MuiTabs-indicator': {
              bgcolor: '#4ade80',
              height: 3
            }
          }}
        >
          <Tab 
            label={
              <Badge badgeContent={pendingChallenges.length} color="warning">
                <Box>Pending Challenges</Box>
              </Badge>
            } 
          />
          <Tab label="All Users" />
          <Tab label="Active Challenges" />
          <Tab label="Admin Users" />
          <Tab label="Challenge Management" />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChartIcon sx={{ fontSize: 18 }} />
                <span>User Trades</span>
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardGiftcardIcon sx={{ fontSize: 18 }} />
                <span>Affiliates</span>
              </Box>
            } 
          />
        </Tabs>
      </Card>

      {/* Tab Content */}
      <Card sx={{ bgcolor: '#1e222d', boxShadow: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Pending Challenges Tab */}
          {currentTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  Pending Challenge Approvals
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchPendingChallenges}
                  disabled={loadingPending}
                  sx={{
                    color: '#4ade80',
                    borderColor: '#4ade80',
                    '&:hover': {
                      borderColor: '#22c55e',
                      bgcolor: 'rgba(74, 222, 128, 0.1)'
                    }
                  }}
                >
                  Refresh
                </Button>
              </Box>

              {loadingPending ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: '#4ade80' }} />
                </Box>
              ) : pendingChallenges.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CheckCircleIcon sx={{ fontSize: 64, color: '#4ade80', mb: 2 }} />
                  <Typography sx={{ color: '#d1d4dc', fontSize: 16, mb: 1 }}>
                    All caught up!
                  </Typography>
                  <Typography sx={{ color: '#888', fontSize: 14 }}>
                    No pending challenges at the moment
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#2a2e39' }}>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Challenge</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Transaction</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Purchase Date</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingChallenges.map((challenge) => (
                        <TableRow 
                          key={challenge.ChallengeID}
                          sx={{ '&:hover': { bgcolor: '#1e222d' } }}
                        >
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                              {challenge.FirstName} {challenge.LastName}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>
                              {challenge.UserEmail}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontSize: 14 }}>
                              {challenge.CustomName || challenge.ChallengeType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#4ade80', fontSize: 14, fontWeight: 600 }}>
                              ${challenge.ChallengeAmount?.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#d1d4dc', fontSize: 13, fontFamily: 'monospace' }}>
                              {challenge.TransactionID}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: 11 }}>
                              {challenge.PaymentEmail}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {formatDate(challenge.PurchaseDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => {
                                  setSelectedChallenge(challenge);
                                  setShowActivateDialog(true);
                                }}
                                disabled={processing}
                                sx={{
                                  bgcolor: '#4ade80',
                                  color: '#fff',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: '#22c55e' },
                                  '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
                                }}
                              >
                                Activate
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<BlockIcon />}
                                onClick={() => {
                                  setSelectedChallenge(challenge);
                                  setShowRejectDialog(true);
                                }}
                                disabled={processing}
                                sx={{
                                  borderColor: '#ef4444',
                                  color: '#ef4444',
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  '&:hover': { 
                                    borderColor: '#dc2626',
                                    bgcolor: 'rgba(239, 68, 68, 0.1)' 
                                  },
                                  '&:disabled': { borderColor: '#2a2e39', color: '#666' }
                                }}
                              >
                                Reject
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* All Users Tab */}
          {currentTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  User Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel sx={{ color: '#888' }}>Platform Filter</InputLabel>
                    <Select
                      value={platformFilter}
                      onChange={(e) => setPlatformFilter(e.target.value)}
                      label="Platform Filter"
                      sx={{
                        color: '#fff',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '.MuiSvgIcon-root': { color: '#888' }
                      }}
                    >
                      <MenuItem value="ALL">All Platforms</MenuItem>
                      <MenuItem value="MT5">MT5 Only</MenuItem>
                      <MenuItem value="XFUNDING">XFUNDING Only</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    onClick={fetchUsers}
                    disabled={loadingUsers}
                    sx={{
                      color: '#4ade80',
                      borderColor: '#4ade80',
                      '&:hover': {
                        borderColor: '#22c55e',
                        bgcolor: 'rgba(74, 222, 128, 0.1)'
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: '#4ade80' }} />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#2a2e39' }}>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>User Details</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Challenges</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>User ID</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users
                        .filter((u) => {
                          if (platformFilter === 'ALL') return true;
                          if (platformFilter === 'MT5') return u.HasMT5Challenge === 1;
                          if (platformFilter === 'XFUNDING') return u.HasXFUNDINGChallenge === 1;
                          return true;
                        })
                        .map((u) => (
                        <TableRow 
                          key={u.CustomerID}
                          sx={{ '&:hover': { bgcolor: '#1e222d' } }}
                        >
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                              {u.FirstName} {u.LastName}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>
                              {u.EmailID}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={u.IsActive ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: u.IsActive ? 'rgba(74, 222, 128, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                color: u.IsActive ? '#4ade80' : '#ef4444',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Box>
                                <Typography sx={{ color: '#4ade80', fontSize: 14, fontWeight: 600 }}>
                                  {u.ActiveChallenges || 0}
                                </Typography>
                                <Typography sx={{ color: '#888', fontSize: 11 }}>Active</Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#fbbf24', fontSize: 14, fontWeight: 600 }}>
                                  {u.PendingChallenges || 0}
                                </Typography>
                                <Typography sx={{ color: '#888', fontSize: 11 }}>Pending</Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#ef4444', fontSize: 14, fontWeight: 600 }}>
                                  {u.RejectedChallenges || 0}
                                </Typography>
                                <Typography sx={{ color: '#888', fontSize: 11 }}>Rejected</Typography>
                              </Box>
                              <Box>
                                <Typography sx={{ color: '#888', fontSize: 14, fontWeight: 600 }}>
                                  {u.TotalChallenges || 0}
                                </Typography>
                                <Typography sx={{ color: '#888', fontSize: 11 }}>Total</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#d1d4dc', fontSize: 13, fontFamily: 'monospace' }}>
                              #{u.CustomerID}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View User Details">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowUserDetailsDialog(true);
                                  }}
                                  sx={{
                                    color: '#3b82f6',
                                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                                    '&:hover': {
                                      bgcolor: 'rgba(59, 130, 246, 0.2)'
                                    }
                                  }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Assign Free Challenge">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowAssignChallengeDialog(true);
                                  }}
                                  sx={{
                                    color: '#10b981',
                                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                                    '&:hover': {
                                      bgcolor: 'rgba(16, 185, 129, 0.2)'
                                    }
                                  }}
                                >
                                  <AssignmentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Impersonate User">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowImpersonateDialog(true);
                                  }}
                                  sx={{
                                    color: '#8b5cf6',
                                    bgcolor: 'rgba(139, 92, 246, 0.1)',
                                    '&:hover': {
                                      bgcolor: 'rgba(139, 92, 246, 0.2)'
                                    }
                                  }}
                                >
                                  <PersonIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={u.IsActive ? 'Deactivate User' : 'Activate User'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowUserStatusDialog(true);
                                  }}
                                  sx={{
                                    color: u.IsActive ? '#ef4444' : '#4ade80',
                                    bgcolor: u.IsActive ? 'rgba(239, 68, 68, 0.1)' : 'rgba(74, 222, 128, 0.1)',
                                    '&:hover': {
                                      bgcolor: u.IsActive ? 'rgba(239, 68, 68, 0.2)' : 'rgba(74, 222, 128, 0.2)'
                                    }
                                  }}
                                >
                                  {u.IsActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reset Password">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setShowPasswordDialog(true);
                                  }}
                                  sx={{
                                    color: '#3b82f6',
                                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                                    '&:hover': {
                                      bgcolor: 'rgba(59, 130, 246, 0.2)'
                                    }
                                  }}
                                >
                                  <LockResetIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {u.HasMT5Challenge === 1 && (
                                <>
                                  <Tooltip title="MT5 Data Entry">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setMT5SelectedUser(u);
                                        setMT5Data({
                                          initialEquity: '',
                                          currentEquity: '',
                                          profit: '',
                                          ordersPlaced: '',
                                          dailyDrawdown: '',
                                          totalDrawdown: '',
                                          hasViolations: 'No',
                                          violationReason: ''
                                        });
                                        setSelectedChallengeId('');
                                        setMT5Username('');
                                        setMT5Password('');
                                        setShowMT5DataDialog(true);
                                        fetchUserMT5Challenges(u.EmailID);
                                      }}
                                      sx={{
                                        color: '#f59e0b',
                                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                                        '&:hover': {
                                          bgcolor: 'rgba(245, 158, 11, 0.2)'
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="View MT5 Data">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setMT5HistoryUser(u);
                                        setShowMT5HistoryDialog(true);
                                        fetchMT5History(u.EmailID);
                                      }}
                                      sx={{
                                        color: '#10b981',
                                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                                        '&:hover': {
                                          bgcolor: 'rgba(16, 185, 129, 0.2)'
                                        }
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Active Challenges Tab */}
          {currentTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  Active Challenges
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Platform Filter */}
                  <Select
                    value={activeChallengesPlatformFilter}
                    onChange={(e) => setActiveChallengesPlatformFilter(e.target.value)}
                    size="small"
                    sx={{
                      color: '#fff',
                      bgcolor: '#2a2e39',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                      '& .MuiSvgIcon-root': { color: '#fff' }
                    }}
                  >
                    <MenuItem value="ALL">All Platforms</MenuItem>
                    <MenuItem value="MT5">MT5 Only</MenuItem>
                    <MenuItem value="XFUNDING">XFUNDING Only</MenuItem>
                  </Select>
                  <Button
                    variant="outlined"
                    onClick={fetchActiveChallenges}
                    disabled={loadingActive}
                    sx={{
                      color: '#4ade80',
                      borderColor: '#4ade80',
                      '&:hover': {
                        borderColor: '#22c55e',
                        bgcolor: 'rgba(74, 222, 128, 0.1)'
                      }
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {loadingActive ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress sx={{ color: '#4ade80' }} />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#2a2e39' }}>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Challenge</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Platform</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Activated</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>User Status</TableCell>
                        <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeChallenges
                        .filter(c => activeChallengesPlatformFilter === 'ALL' || c.Platform === activeChallengesPlatformFilter)
                        .map((challenge) => (
                        <TableRow 
                          key={challenge.ChallengeID}
                          sx={{ '&:hover': { bgcolor: '#1e222d' } }}
                        >
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                              {challenge.FirstName} {challenge.LastName}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>
                              {challenge.UserEmail}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontSize: 14 }}>
                              {challenge.CustomName || challenge.ChallengeType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={challenge.Platform || 'MT5'}
                              size="small"
                              sx={{
                                bgcolor: challenge.Platform === 'MT5' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                                color: challenge.Platform === 'MT5' ? '#f59e0b' : '#3b82f6',
                                fontWeight: 600,
                                fontSize: 11
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#4ade80', fontSize: 14, fontWeight: 600 }}>
                              ${challenge.ChallengeAmount?.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {formatDate(challenge.ActivationDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={challenge.UserIsActive ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: challenge.UserIsActive ? 'rgba(74, 222, 128, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                color: challenge.UserIsActive ? '#4ade80' : '#ef4444',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Deactivate Challenge">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedChallenge(challenge);
                                  setShowDeactivateDialog(true);
                                }}
                                sx={{
                                  color: '#ef4444',
                                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                                  '&:hover': {
                                    bgcolor: 'rgba(239, 68, 68, 0.2)'
                                  }
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Admin Users Tab */}
          {currentTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Admin Users
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={fetchAdminUsers}
                  disabled={loadingAdmins}
                  sx={{
                    color: '#4ade80',
                    borderColor: '#4ade80',
                    '&:hover': {
                      borderColor: '#22c55e',
                      bgcolor: 'rgba(74, 222, 128, 0.1)'
                    }
                  }}
                >
                  {loadingAdmins ? <CircularProgress size={20} /> : 'Refresh'}
                </Button>
              </Box>

              {loadingAdmins ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : adminUsers.length === 0 ? (
                <Typography sx={{ color: '#888', textAlign: 'center', py: 5 }}>
                  No admin users found
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722', boxShadow: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#1e222d' }}>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13 }}>Admin ID</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13 }}>Email</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13 }}>First Name</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13 }}>Last Name</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13 }}>Role</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 13, textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adminUsers.map((admin) => (
                        <TableRow 
                          key={admin.AdminID}
                          sx={{ 
                            '&:hover': { bgcolor: '#1e222d' },
                            borderBottom: '1px solid #2a2e39'
                          }}
                        >
                          <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                            #{admin.AdminID}
                          </TableCell>
                          <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                            {admin.EmailID}
                          </TableCell>
                          <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                            {admin.FirstName}
                          </TableCell>
                          <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                            {admin.LastName || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>
                            <Chip 
                              label="Admin" 
                              size="small"
                              sx={{
                                bgcolor: 'rgba(59, 130, 246, 0.2)',
                                color: '#3b82f6',
                                fontWeight: 600,
                                fontSize: 11
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Tooltip title="Reset Password" arrow>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedAdmin(admin);
                                  setShowAdminPasswordDialog(true);
                                }}
                                sx={{
                                  color: '#f59e0b',
                                  bgcolor: 'rgba(245, 158, 11, 0.1)',
                                  '&:hover': {
                                    bgcolor: 'rgba(245, 158, 11, 0.2)'
                                  }
                                }}
                              >
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Challenge Management Tab */}
          {currentTab === 4 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  Challenge Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    select
                    size="small"
                    value={phaseFilter}
                    onChange={(e) => setPhaseFilter(e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': { borderColor: '#3a3e49' },
                        '&:hover fieldset': { borderColor: '#4a4e59' }
                      }
                    }}
                  >
                    <option value="All">All Phases</option>
                    <option value="Phase1">Phase 1</option>
                    <option value="Phase2">Phase 2</option>
                    <option value="Funded">Funded</option>
                    <option value="Failed">Failed</option>
                  </TextField>
                  <Button
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={fetchAllChallengesWithProgress}
                    sx={{ color: '#4ade80', borderColor: '#4ade80' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {loadingChallengeProgress ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#4ade80' }} />
                </Box>
              ) : allChallengesWithProgress.length === 0 ? (
                <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#64b5f6' }}>
                  No challenges found
                </Alert>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722', maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Challenge ID</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>User</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>MT5 UserID</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Phase</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Profit %</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Daily DD</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Total DD</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Scalping</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allChallengesWithProgress
                        .filter(ch => phaseFilter === 'All' || ch.CurrentPhase === phaseFilter)
                        .map((challenge) => {
                          const profitColor = challenge.CurrentProfitPercent >= 0 ? '#10b981' : '#ef4444';
                          const ddDailyColor = challenge.DailyDrawdownPercent >= 4 ? '#ef4444' : challenge.DailyDrawdownPercent >= 2.5 ? '#f59e0b' : '#10b981';
                          const ddTotalColor = challenge.TotalDrawdownPercent >= 8 ? '#ef4444' : challenge.TotalDrawdownPercent >= 5 ? '#f59e0b' : '#10b981';
                          
                          return (
                            <TableRow key={challenge.ChallengeID} sx={{ '&:hover': { bgcolor: '#1e222d' } }}>
                              <TableCell sx={{ color: '#d1d4dc' }}>#{challenge.ChallengeID}</TableCell>
                              <TableCell sx={{ color: '#d1d4dc' }}>
                                <Box>
                                  <Typography variant="body2">{challenge.FirstName} {challenge.LastName}</Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>{challenge.EmailID}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: '#d1d4dc' }}>
                                {challenge.MT5Username || '-'}
                              </TableCell>
                              <TableCell sx={{ color: '#d1d4dc' }}>{challenge.ChallengeType}</TableCell>
                              <TableCell sx={{ color: '#4ade80', fontWeight: 600 }}>
                                ${challenge.ChallengeAmount?.toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={challenge.CurrentPhase}
                                  size="small"
                                  sx={{
                                    bgcolor: challenge.CurrentPhase === 'Phase1' ? 'rgba(33, 150, 243, 0.2)' :
                                             challenge.CurrentPhase === 'Phase2' ? 'rgba(76, 175, 80, 0.2)' :
                                             challenge.CurrentPhase === 'Funded' ? 'rgba(255, 215, 0, 0.2)' :
                                             'rgba(244, 67, 54, 0.2)',
                                    color: challenge.CurrentPhase === 'Phase1' ? '#2196f3' :
                                           challenge.CurrentPhase === 'Phase2' ? '#4caf50' :
                                           challenge.CurrentPhase === 'Funded' ? '#ffd700' :
                                           '#f44336',
                                    border: `1px solid ${challenge.CurrentPhase === 'Phase1' ? '#2196f3' :
                                                        challenge.CurrentPhase === 'Phase2' ? '#4caf50' :
                                                        challenge.CurrentPhase === 'Funded' ? '#ffd700' :
                                                        '#f44336'}`
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: profitColor, fontWeight: 600 }}>
                                  {challenge.CurrentProfitPercent?.toFixed(2)}% / {challenge.CurrentPhaseTargetPercent}%
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: ddDailyColor, fontWeight: 600 }}>
                                  {challenge.DailyDrawdownPercent?.toFixed(2)}% / 5%
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ color: ddTotalColor, fontWeight: 600 }}>
                                  {challenge.TotalDrawdownPercent?.toFixed(2)}% / 10%
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {challenge.ScalpingViolationsCount > 0 ? (
                                  <Chip
                                    label={challenge.ScalpingViolationsCount}
                                    size="small"
                                    icon={<WarningIcon />}
                                    sx={{ bgcolor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ color: '#666' }}>None</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={challenge.Status}
                                  size="small"
                                  color={challenge.Status === 'Active' ? 'success' : 'warning'}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {challenge.Status === 'Active' && challenge.CurrentPhase !== 'Failed' && challenge.CurrentPhase !== 'Funded' && (
                                    <Tooltip title="Advance Phase">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedChallenge(challenge);
                                          setShowAdvancePhaseDialog(true);
                                        }}
                                        sx={{
                                          color: '#4caf50',
                                          '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' }
                                        }}
                                      >
                                        <FastForwardIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {challenge.ScalpingViolationsCount > 0 && (
                                    <Tooltip title="View Scalping Violations">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedChallenge(challenge);
                                          fetchScalpingViolations(challenge.ChallengeID);
                                        }}
                                        sx={{
                                          color: '#f59e0b',
                                          '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.2)' }
                                        }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {challenge.Status === 'Active' && challenge.CurrentPhase !== 'Failed' && (
                                    <Tooltip title="Fail Challenge">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedChallenge(challenge);
                                          setShowFailChallengeDialog(true);
                                        }}
                                        sx={{
                                          color: '#ef4444',
                                          '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' }
                                        }}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {(challenge.CurrentPhase === 'Failed' || challenge.Status === 'Expired') && (
                                    <Tooltip title="Reset Challenge">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedChallenge(challenge);
                                          setShowResetChallengeDialog(true);
                                        }}
                                        sx={{
                                          color: '#2196f3',
                                          '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.2)' }
                                        }}
                                      >
                                        <RestartAltIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Feature 2: User Trades Tab */}
          {currentTab === 5 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
                User Trading History
              </Typography>

              {/* Filters Section */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* User Selection - Full Width */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>
                      Select User
                    </Typography>
                    <Autocomplete
                      fullWidth
                      options={allUsers}
                      getOptionLabel={(option) => `${option.fullName || 'No Name'} (${option.email})`}
                      value={allUsers.find(u => u.email === selectedTradeUser) || null}
                      onChange={(event, newValue) => {
                        const email = newValue ? newValue.email : '';
                        setSelectedTradeUser(email);
                        setUserSearchQuery('');
                        if (email) {
                          fetchUserTrades(email);
                        }
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.email} style={{ padding: '12px 16px' }}>
                          <Box sx={{ width: '100%' }}>
                            <Typography sx={{ color: '#d1d4dc', fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                              {option.fullName || 'No Name'}
                            </Typography>
                            <Typography sx={{ color: '#888', fontSize: 13, wordBreak: 'break-all' }}>
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search by name or email..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: '#131722',
                              color: '#fff',
                              minHeight: 56,
                              paddingRight: '85px !important',
                              '& fieldset': { borderColor: '#2a2e39' },
                              '&:hover fieldset': { borderColor: '#4ade80' },
                              '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                            },
                            '& .MuiInputBase-input': { 
                              color: '#fff !important', 
                              fontSize: '14px !important',
                              overflow: 'visible !important',
                              textOverflow: 'clip !important'
                            },
                            '& .MuiAutocomplete-input': {
                              minWidth: '0 !important',
                              width: 'auto !important'
                            },
                            '& .MuiInputBase-input::placeholder': { color: '#888', opacity: 1 }
                          }}
                        />
                      )}
                      ListboxProps={{
                        style: { 
                          maxHeight: 400, 
                          bgcolor: '#1e222d'
                        },
                        sx: {
                          bgcolor: '#1e222d',
                          '& .MuiAutocomplete-option': {
                            color: '#d1d4dc',
                            '&:hover': { bgcolor: '#2a2e39' },
                            '&[aria-selected="true"]': { 
                              bgcolor: '#2a2e39 !important',
                              '&:hover': { bgcolor: '#363a45 !important' }
                            }
                          }
                        }
                      }}
                      sx={{
                        '& .MuiAutocomplete-popupIndicator': { color: '#888' },
                        '& .MuiAutocomplete-clearIndicator': { color: '#888' }
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            bgcolor: '#1e222d',
                            border: '1px solid #2a2e39',
                            minWidth: '500px'
                          }
                        }
                      }}
                    />
                  </Box>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>
                      Status
                    </Typography>
                    <Select
                      fullWidth
                      value={tradesStatusFilter}
                      onChange={(e) => setTradesStatusFilter(e.target.value)}
                      sx={{
                        bgcolor: '#131722',
                        color: '#fff',
                        height: 56,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '& .MuiSelect-icon': { color: '#888' }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e222d',
                            '& .MuiMenuItem-root': {
                              color: '#d1d4dc',
                              '&:hover': { bgcolor: '#2a2e39' },
                              '&.Mui-selected': { bgcolor: '#2a2e39', '&:hover': { bgcolor: '#363a45' } }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value="ALL">All Statuses</MenuItem>
                      <MenuItem value="OPEN">Open</MenuItem>
                      <MenuItem value="CLOSED">Closed</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                    </Select>
                  </Box>
                </Grid>

                {/* Challenge Filter */}
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>
                      Challenge
                    </Typography>
                    <Select
                      fullWidth
                      value={tradesChallengeFilter}
                      onChange={(e) => setTradesChallengeFilter(e.target.value)}
                      sx={{
                        bgcolor: '#131722',
                        color: '#fff',
                        height: 56,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '& .MuiSelect-icon': { color: '#888' }
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#1e222d',
                            '& .MuiMenuItem-root': {
                              color: '#d1d4dc',
                              '&:hover': { bgcolor: '#2a2e39' },
                              '&.Mui-selected': { bgcolor: '#2a2e39', '&:hover': { bgcolor: '#363a45' } }
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem value="ALL">All Challenges</MenuItem>
                      {userTrades.length > 0 && 
                        [...new Set(userTrades.map(t => t.ChallengeID).filter(Boolean))]
                          .map(cid => (
                            <MenuItem key={cid} value={cid}>
                              Challenge #{cid}
                            </MenuItem>
                          ))
                      }
                    </Select>
                  </Box>
                </Grid>

                {/* Refresh Button */}
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => fetchUserTrades(selectedTradeUser)}
                    disabled={!selectedTradeUser || loadingTrades}
                    sx={{
                      height: 56,
                      bgcolor: '#4ade80',
                      color: '#000',
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#22c55e' },
                      '&:disabled': { bgcolor: '#2a2e39', color: '#555' }
                    }}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>

              {/* Summary Cards */}
              {tradesSummary && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: '#131722', p: 2, textAlign: 'center' }}>
                      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>Total Trades</Typography>
                      <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                        {tradesSummary.totalTrades}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: '#131722', p: 2, textAlign: 'center' }}>
                      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>Total P/L</Typography>
                      <Typography 
                        sx={{ 
                          color: tradesSummary.totalPL >= 0 ? '#4ade80' : '#ef4444', 
                          fontSize: 24, 
                          fontWeight: 700 
                        }}
                      >
                        ${tradesSummary.totalPL?.toFixed(2)}
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: '#131722', p: 2, textAlign: 'center' }}>
                      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>Win Rate</Typography>
                      <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                        {tradesSummary.closedTrades > 0 
                          ? ((tradesSummary.winningTrades / tradesSummary.closedTrades) * 100).toFixed(1)
                          : 0}%
                      </Typography>
                      <Typography sx={{ color: '#666', fontSize: 11 }}>
                        {tradesSummary.winningTrades}W / {tradesSummary.losingTrades}L
                      </Typography>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card sx={{ bgcolor: '#131722', p: 2, textAlign: 'center' }}>
                      <Typography sx={{ color: '#888', fontSize: 12, mb: 1 }}>Scalping Violations</Typography>
                      <Typography 
                        sx={{ 
                          color: tradesSummary.scalpingViolations > 0 ? '#ef4444' : '#4ade80', 
                          fontSize: 24, 
                          fontWeight: 700 
                        }}
                      >
                        {tradesSummary.scalpingViolations}
                      </Typography>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Trades Table */}
              {loadingTrades ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#4ade80' }} />
                </Box>
              ) : !selectedTradeUser ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ShowChartIcon sx={{ fontSize: 64, color: '#2a2e39', mb: 2 }} />
                  <Typography sx={{ color: '#888', fontSize: 16 }}>
                    Select a user to view their trading history
                  </Typography>
                </Box>
              ) : userTrades.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography sx={{ color: '#888', fontSize: 16 }}>
                    No trades found for this user
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#131722', maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Order ID</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Symbol</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Type</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Volume</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Entry</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Exit</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>P/L</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Challenge</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Open Time</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Close Time</TableCell>
                        <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600, fontSize: 12 }}>Duration</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userTrades.map((trade) => {
                        const openTime = trade.OrderOpenTime ? new Date(trade.OrderOpenTime) : null;
                        const closeTime = trade.OrderCloseTime ? new Date(trade.OrderCloseTime) : null;
                        const duration = openTime && closeTime 
                          ? Math.floor((closeTime - openTime) / 1000 / 60) // minutes
                          : null;

                        return (
                          <TableRow 
                            key={trade.OrderID}
                            sx={{ 
                              '&:hover': { bgcolor: '#1e222d' },
                              bgcolor: '#131722'
                            }}
                          >
                            <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {trade.OrderID}
                            </TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>
                              {trade.Symbol}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={trade.OrderType}
                                size="small"
                                sx={{
                                  bgcolor: trade.OrderType === 'BUY' ? '#10b98120' : '#ef444420',
                                  color: trade.OrderType === 'BUY' ? '#10b981' : '#ef4444',
                                  fontSize: 11,
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {trade.Volume}
                            </TableCell>
                            <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {trade.OrderOpenPrice?.toFixed(5)}
                            </TableCell>
                            <TableCell sx={{ color: '#d1d4dc', fontSize: 13 }}>
                              {trade.OrderClosePrice?.toFixed(5) || '-'}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                sx={{ 
                                  color: trade.ProfitLoss >= 0 ? '#4ade80' : '#ef4444',
                                  fontWeight: 600,
                                  fontSize: 13
                                }}
                              >
                                {trade.ProfitLoss 
                                  ? `$${trade.ProfitLoss.toFixed(2)}`
                                  : '-'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={trade.Status}
                                size="small"
                                sx={{
                                  bgcolor: 
                                    trade.Status === 'OPEN' ? '#3b82f620' :
                                    trade.Status === 'CLOSED' ? '#4ade8020' :
                                    '#f59e0b20',
                                  color: 
                                    trade.Status === 'OPEN' ? '#3b82f6' :
                                    trade.Status === 'CLOSED' ? '#4ade80' :
                                    '#f59e0b',
                                  fontSize: 11,
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#888', fontSize: 12 }}>
                              {trade.ChallengeID ? `#${trade.ChallengeID}` : '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#888', fontSize: 12 }}>
                              {openTime ? openTime.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#888', fontSize: 12 }}>
                              {closeTime ? closeTime.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell sx={{ color: '#888', fontSize: 12 }}>
                              {duration !== null 
                                ? duration < 60 
                                  ? `${duration}m`
                                  : `${Math.floor(duration / 60)}h ${duration % 60}m`
                                : '-'
                              }
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {userTrades.length > 0 && (
                <Typography sx={{ color: '#666', fontSize: 12, mt: 2, textAlign: 'center' }}>
                  Showing latest {userTrades.length} trades (max 50)
                </Typography>
              )}
            </Box>
          )}

          {/* Affiliates Tab */}
          {currentTab === 6 && (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600 }}>
                  Affiliate Program
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setShowAddAffiliateDialog(true)}
                  sx={{
                    bgcolor: '#9333ea',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#7e22ce' }
                  }}
                >
                  Add Affiliate
                </Button>
              </Box>

              {/* Affiliates Statistics Cards */}
              {affiliates.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#1e222d', border: '1px solid #9333ea' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CardGiftcardIcon sx={{ fontSize: 40, color: '#9333ea' }} />
                          <Box>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>Total Affiliates</Typography>
                            <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                              {affiliates.length}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#1e222d', border: '1px solid #10b981' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <PeopleIcon sx={{ fontSize: 40, color: '#10b981' }} />
                          <Box>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>Total Referral Signups</Typography>
                            <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                              {affiliates.reduce((sum, aff) => sum + (aff.totalSignups || 0), 0)}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: '#1e222d', border: '1px solid #3b82f6' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CheckCircleIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                          <Box>
                            <Typography sx={{ color: '#888', fontSize: 12 }}>Active Affiliates</Typography>
                            <Typography sx={{ color: '#fff', fontSize: 24, fontWeight: 700 }}>
                              {affiliates.filter(aff => aff.isActive).length}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Affiliates Table */}
              {loadingAffiliates ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                  <CircularProgress sx={{ color: '#9333ea' }} />
                </Box>
              ) : affiliates.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CardGiftcardIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
                  <Typography sx={{ color: '#888', fontSize: 18, mb: 1 }}>
                    No affiliates yet
                  </Typography>
                  <Typography sx={{ color: '#666', fontSize: 14 }}>
                    Click "Add Affiliate" to create your first affiliate partner
                  </Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ bgcolor: '#1e222d', border: '1px solid #2a2e39' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#131722' }}>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Email</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Referral Code</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Status</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Expiry Date</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Total Signups</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Active Users</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Created Date</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Created By</TableCell>
                        <TableCell sx={{ color: '#888', fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {affiliates.map((affiliate) => (
                        <TableRow 
                          key={affiliate.affiliateId}
                          sx={{ 
                            '&:hover': { bgcolor: '#2a2e39' },
                            opacity: affiliate.isActive ? 1 : 0.5
                          }}
                        >
                          <TableCell sx={{ color: '#d1d4dc' }}>{affiliate.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={affiliate.referralCode}
                                size="small"
                                sx={{
                                  bgcolor: '#9333ea20',
                                  color: '#9333ea',
                                  fontWeight: 600,
                                  fontFamily: 'monospace'
                                }}
                              />
                              <Tooltip title="Copy Code">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(affiliate.referralCode);
                                    setSuccessMessage('Referral code copied!');
                                  }}
                                  sx={{ color: '#666', '&:hover': { color: '#9333ea' } }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={affiliate.isActive ? 'Active' : 'Disabled'}
                              size="small"
                              color={affiliate.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {affiliate.expiryDate ? (
                              <Box>
                                <Typography sx={{ 
                                  color: affiliate.isExpired ? '#ef4444' : '#888', 
                                  fontSize: 13,
                                  fontWeight: affiliate.isExpired ? 600 : 400
                                }}>
                                  {new Date(affiliate.expiryDate).toLocaleDateString()}
                                </Typography>
                                {affiliate.isExpired && (
                                  <Chip
                                    label="Expired"
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#ef444420',
                                      color: '#ef4444',
                                      height: 20,
                                      fontSize: 10,
                                      mt: 0.5
                                    }}
                                  />
                                )}
                              </Box>
                            ) : (
                              <Typography sx={{ color: '#666', fontSize: 13 }}>
                                No Expiry
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#fff', fontWeight: 600 }}>
                              {affiliate.totalSignups}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#4ade80', fontWeight: 600 }}>
                              {affiliate.activeUsers}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#888', fontSize: 13 }}>
                            {new Date(affiliate.createdDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ color: '#888', fontSize: 13 }}>
                            {affiliate.createdBy || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    setSelectedAffiliate(affiliate);
                                    // Fetch affiliate details
                                    try {
                                      const response = await fetch(
                                        getApiUrl(`/api/admin/affiliates/stats/${affiliate.affiliateId}`),
                                        { headers: getAuthHeaders() }
                                      );
                                      const data = await response.json();
                                      if (data.success) {
                                        setAffiliateUsers(data.users);
                                        setShowAffiliateDetailsDialog(true);
                                      }
                                    } catch (error) {
                                      console.error('Error fetching affiliate details:', error);
                                    }
                                  }}
                                  sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#3b82f620' } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={affiliate.isActive ? 'Disable' : 'Enable'}>
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    try {
                                      const response = await fetch(
                                        getApiUrl('/api/admin/affiliates/toggle-status'),
                                        {
                                          method: 'POST',
                                          headers: getAuthHeaders(),
                                          body: JSON.stringify({ affiliateId: affiliate.affiliateId })
                                        }
                                      );
                                      const data = await response.json();
                                      if (data.success) {
                                        setSuccessMessage(data.message);
                                        // Refresh affiliates list
                                        fetchAffiliates();
                                      } else {
                                        setErrorMessage(data.error || 'Failed to update status');
                                      }
                                    } catch (error) {
                                      console.error('Error toggling affiliate status:', error);
                                      setErrorMessage('Failed to update affiliate status');
                                    }
                                  }}
                                  sx={{
                                    color: affiliate.isActive ? '#ef4444' : '#4ade80',
                                    '&:hover': {
                                      bgcolor: affiliate.isActive ? '#ef444420' : '#4ade8020'
                                    }
                                  }}
                                >
                                  {affiliate.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Extend Expiry (3 months)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExtendExpiry(affiliate.affiliateId, 3)}
                                  sx={{
                                    color: '#f59e0b',
                                    '&:hover': { bgcolor: '#f59e0b20' }
                                  }}
                                >
                                  <UpdateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Activate Challenge Dialog */}
      <Dialog 
        open={showActivateDialog} 
        onClose={() => setShowActivateDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          Confirm Challenge Activation
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedChallenge && (
            <Box>
              <Typography sx={{ color: '#d1d4dc', mb: 2 }}>
                Are you sure you want to activate this challenge?
              </Typography>
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1.5 }}>
                  {selectedChallenge.FirstName} {selectedChallenge.LastName} ({selectedChallenge.UserEmail})
                </Typography>
                
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Challenge</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1.5 }}>
                  {selectedChallenge.CustomName || selectedChallenge.ChallengeType} - ${selectedChallenge.ChallengeAmount?.toLocaleString()}
                </Typography>
                
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Transaction ID</Typography>
                <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>
                  {selectedChallenge.TransactionID}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowActivateDialog(false)}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleActivateChallenge}
            variant="contained"
            sx={{
              bgcolor: '#4ade80',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#22c55e' }
            }}
          >
            Activate Challenge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Challenge Dialog */}
      <Dialog 
        open={showRejectDialog} 
        onClose={() => !processing && setShowRejectDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 450 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39', display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon sx={{ color: '#ef4444' }} />
          Reject Pending Challenge
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedChallenge && (
            <Box>
              <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', mb: 3 }}>
                This will reject the pending challenge. The user will not be able to trade with this challenge.
              </Alert>
              
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39', mb: 3 }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1.5 }}>
                  {selectedChallenge.FirstName} {selectedChallenge.LastName} ({selectedChallenge.UserEmail})
                </Typography>
                
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Challenge</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1.5 }}>
                  {selectedChallenge.CustomName || selectedChallenge.ChallengeType} - ${selectedChallenge.ChallengeAmount?.toLocaleString()}
                </Typography>
                
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Transaction ID</Typography>
                <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: 'monospace' }}>
                  {selectedChallenge.TransactionID}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Rejection Reason (Optional)"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                multiline
                rows={3}
                placeholder="E.g., Payment verification failed, Duplicate purchase, etc."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    bgcolor: '#131722',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#4a4e59' },
                    '&.Mui-focused fieldset': { borderColor: '#ef4444' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowRejectDialog(false);
              setRejectionReason('');
            }}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleRejectChallenge}
            disabled={processing}
            variant="contained"
            sx={{
              bgcolor: '#ef4444',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#dc2626' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Reject Challenge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Challenge Dialog */}
      <Dialog 
        open={showDeactivateDialog} 
        onClose={() => setShowDeactivateDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ef4444' }} />
          Deactivate Challenge
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedChallenge && (
            <Box>
              <Typography sx={{ color: '#d1d4dc', mb: 2 }}>
                Are you sure you want to deactivate this challenge? This will prevent the user from trading.
              </Typography>
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1.5 }}>
                  {selectedChallenge.FirstName} {selectedChallenge.LastName}
                </Typography>
                
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Challenge</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14 }}>
                  {selectedChallenge.CustomName || selectedChallenge.ChallengeType}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowDeactivateDialog(false)}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeactivateChallenge}
            variant="contained"
            sx={{
              bgcolor: '#ef4444',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#dc2626' }
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Status Toggle Dialog */}
      <Dialog 
        open={showUserStatusDialog} 
        onClose={() => setShowUserStatusDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          {selectedUser?.IsActive ? 'Deactivate User' : 'Activate User'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedUser && (
            <Box>
              <Typography sx={{ color: '#d1d4dc', mb: 2 }}>
                {selectedUser.IsActive 
                  ? 'Deactivating this user will prevent them from logging in.'
                  : 'This will allow the user to log in again.'}
              </Typography>
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1 }}>
                  {selectedUser.FirstName} {selectedUser.LastName}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: 13 }}>
                  {selectedUser.EmailID}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowUserStatusDialog(false)}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleToggleUserStatus}
            variant="contained"
            sx={{
              bgcolor: selectedUser?.IsActive ? '#ef4444' : '#4ade80',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { 
                bgcolor: selectedUser?.IsActive ? '#dc2626' : '#22c55e'
              }
            }}
          >
            {selectedUser?.IsActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog 
        open={showUserDetailsDialog} 
        onClose={() => setShowUserDetailsDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 500 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#3b82f6' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>User Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Name */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BadgeIcon sx={{ color: '#4ade80', fontSize: 20 }} />
                  <Typography sx={{ color: '#888', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    First Name
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                  <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                    {selectedUser.FirstName || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {/* Last Name */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <BadgeIcon sx={{ color: '#4ade80', fontSize: 20 }} />
                  <Typography sx={{ color: '#888', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Last Name
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                  <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                    {selectedUser.LastName || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {/* Email ID */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <EmailIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                  <Typography sx={{ color: '#888', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Email Address
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                  <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500, wordBreak: 'break-all' }}>
                    {selectedUser.EmailID || 'N/A'}
                  </Typography>
                </Box>
              </Box>

              {/* Mobile Number */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PhoneIcon sx={{ color: '#fbbf24', fontSize: 20 }} />
                  <Typography sx={{ color: '#888', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Phone Number
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39' }}>
                  <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 500 }}>
                    {selectedUser.MobileNumber || 'Not Provided'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowUserDetailsDialog(false)}
            variant="contained"
            sx={{
              bgcolor: '#3b82f6',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#2563eb' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog 
        open={showPasswordDialog} 
        onClose={() => {
          setShowPasswordDialog(false);
          setNewPassword('');
        }}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          Reset User Password
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedUser && (
            <Box>
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39', mb: 3 }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1 }}>
                  {selectedUser.FirstName} {selectedUser.LastName}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: 13 }}>
                  {selectedUser.EmailID}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Minimum 6 characters"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' },
                  '& .MuiFormHelperText-root': { color: '#888' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowPasswordDialog(false);
              setNewPassword('');
            }}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword}
            variant="contained"
            disabled={!newPassword || newPassword.length < 6}
            sx={{
              bgcolor: '#3b82f6',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#2563eb' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Password Reset Dialog */}
      <Dialog 
        open={showAdminPasswordDialog} 
        onClose={() => setShowAdminPasswordDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          Reset Admin Password
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedAdmin && (
            <Box>
              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39', mb: 3 }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Admin</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1 }}>
                  {selectedAdmin.FirstName} {selectedAdmin.LastName}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: 13 }}>
                  {selectedAdmin.EmailID}
                </Typography>
              </Box>
              
              <Alert severity="warning" sx={{ mb: 3, bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                <Typography sx={{ fontSize: 13 }}>
                  You are about to reset the password for an admin user. Make sure to communicate the new password securely.
                </Typography>
              </Alert>

              <TextField
                label="New Password"
                type="password"
                fullWidth
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                helperText="Password must be at least 6 characters"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' },
                  '& .MuiFormHelperText-root': { color: '#888' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowAdminPasswordDialog(false);
              setNewAdminPassword('');
            }}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetAdminPassword}
            variant="contained"
            disabled={!newAdminPassword || newAdminPassword.length < 6}
            sx={{
              bgcolor: '#f59e0b',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#d97706' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            Reset Admin Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Free Challenge Dialog */}
      <Dialog 
        open={showAssignChallengeDialog} 
        onClose={() => !processing && setShowAssignChallengeDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', borderRadius: 2, minWidth: 500 } }}
      >
        <DialogTitle sx={{ bgcolor: '#131722', color: '#fff', borderBottom: '1px solid #2a2e39', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ color: '#10b981' }} />
          Assign Free Challenge
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', pt: 3 }}>
          {selectedUser && (
            <Box>
              <Alert severity="info" sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', mb: 3 }}>
                Assigning a free challenge to <strong>{selectedUser.FirstName} {selectedUser.LastName}</strong>. 
                This challenge will be automatically activated and ready for trading.
              </Alert>

              <Box sx={{ bgcolor: '#131722', p: 2, borderRadius: 1, border: '1px solid #2a2e39', mb: 3 }}>
                <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>User</Typography>
                <Typography sx={{ color: '#fff', fontSize: 14, mb: 1 }}>
                  {selectedUser.FirstName} {selectedUser.LastName}
                </Typography>
                <Typography sx={{ color: '#888', fontSize: 13 }}>
                  {selectedUser.EmailID}
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel sx={{ color: '#888' }}>Challenge Type *</InputLabel>
                <Select
                  value={freeChallengeType}
                  onChange={(e) => setFreeChallengeType(e.target.value)}
                  label="Challenge Type *"
                  sx={{
                    bgcolor: '#131722',
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#10b981' },
                    '& .MuiSelect-icon': { color: '#888' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1e222d',
                        '& .MuiMenuItem-root': {
                          color: '#d1d4dc',
                          '&:hover': { bgcolor: '#2a2e39' },
                          '&.Mui-selected': { bgcolor: '#2a2e39', '&:hover': { bgcolor: '#363a45' } }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="25K">$25,000 Challenge</MenuItem>
                  <MenuItem value="50K">$50,000 Challenge</MenuItem>
                  <MenuItem value="100K">$100,000 Challenge</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Custom Name (Optional)"
                value={freeChallengeCustomName}
                onChange={(e) => setFreeChallengeCustomName(e.target.value)}
                placeholder={`Free ${freeChallengeType} Challenge`}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    bgcolor: '#131722',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#10b981' },
                    '&.Mui-focused fieldset': { borderColor: '#10b981' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              />

              <TextField
                fullWidth
                label="Notes/Reason (Optional)"
                value={freeChallengeNotes}
                onChange={(e) => setFreeChallengeNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="E.g., Promotional offer, Compensation, Beta testing..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    bgcolor: '#131722',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#10b981' },
                    '&.Mui-focused fieldset': { borderColor: '#10b981' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#131722', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowAssignChallengeDialog(false);
              setFreeChallengeType('5K');
              setFreeChallengeCustomName('');
              setFreeChallengeNotes('');
            }}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssignFreeChallenge}
            disabled={processing}
            variant="contained"
            sx={{
              bgcolor: '#10b981',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#059669' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Assign Challenge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonate User Dialog */}
      <Dialog 
        open={showImpersonateDialog} 
        onClose={() => setShowImpersonateDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1e222d',
            backgroundImage: 'none',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid #2a2e39'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #2a2e39',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PersonIcon sx={{ color: '#8b5cf6' }} />
          Impersonate User
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You will be logged in as this user and can perform actions on their behalf.
          </Alert>
          {selectedUser && (
            <Box>
              <Typography variant="body2" sx={{ color: '#9ca3af', mb: 1 }}>
                User Details:
              </Typography>
              <Box sx={{ bgcolor: '#2a2e39', p: 2, borderRadius: 1 }}>
                <Typography sx={{ color: '#fff', mb: 0.5 }}>
                  <strong>Name:</strong> {selectedUser.FirstName} {selectedUser.LastName}
                </Typography>
                <Typography sx={{ color: '#fff', mb: 0.5 }}>
                  <strong>Email:</strong> {selectedUser.EmailID}
                </Typography>
                <Typography sx={{ color: '#fff' }}>
                  <strong>Customer ID:</strong> #{selectedUser.CustomerID}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#9ca3af', mt: 2 }}>
                Session will expire in 1 hour. Click "Exit Impersonation" at any time to return to admin panel.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => setShowImpersonateDialog(false)}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImpersonateUser}
            disabled={processing}
            variant="contained"
            sx={{
              bgcolor: '#8b5cf6',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#7c3aed' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Start Impersonation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Advance Phase Dialog */}
      <Dialog 
        open={showAdvancePhaseDialog} 
        onClose={() => !processing && setShowAdvancePhaseDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', color: '#fff', minWidth: 400 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2a2e39', fontWeight: 600 }}>
          Advance Challenge Phase
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedChallenge && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to advance this challenge?
              </Typography>
              <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#64b5f6', mb: 2 }}>
                <strong>Challenge:</strong> {selectedChallenge.ChallengeType} - ${selectedChallenge.ChallengeAmount?.toLocaleString()}<br />
                <strong>User:</strong> {selectedChallenge.FirstName} {selectedChallenge.LastName}<br />
                <strong>Current Phase:</strong> {selectedChallenge.CurrentPhase}<br />
                <strong>Next Action:</strong> {selectedChallenge.CurrentPhase === 'Phase1' ? 'Advance to Phase 2 (balance will reset)' : 'Mark as Funded'}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          <Button 
            onClick={() => setShowAdvancePhaseDialog(false)}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAdvancePhase}
            disabled={processing}
            variant="contained"
            sx={{
              bgcolor: '#4caf50',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#45a049' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Advance Phase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fail Challenge Dialog */}
      <Dialog 
        open={showFailChallengeDialog} 
        onClose={() => !processing && setShowFailChallengeDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', color: '#fff', minWidth: 450 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2a2e39', fontWeight: 600 }}>
          Fail Challenge
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedChallenge && (
            <>
              <Alert severity="error" sx={{ bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', mb: 3 }}>
                <strong>Challenge:</strong> {selectedChallenge.ChallengeType} - ${selectedChallenge.ChallengeAmount?.toLocaleString()}<br />
                <strong>User:</strong> {selectedChallenge.FirstName} {selectedChallenge.LastName}
              </Alert>
              <TextField
                fullWidth
                label="Failure Reason"
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                multiline
                rows={3}
                required
                placeholder="E.g., Excessive risk, Policy violation, etc."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#3a3e49' },
                    '&:hover fieldset': { borderColor: '#4a4e59' },
                    '&.Mui-focused fieldset': { borderColor: '#ef4444' }
                  },
                  '& .MuiInputLabel-root': { color: '#888' }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          <Button 
            onClick={() => {
              setShowFailChallengeDialog(false);
              setFailureReason('');
            }}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleFailChallenge}
            disabled={processing || !failureReason.trim()}
            variant="contained"
            sx={{
              bgcolor: '#ef4444',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#dc2626' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Fail Challenge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Challenge Dialog */}
      <Dialog 
        open={showResetChallengeDialog} 
        onClose={() => !processing && setShowResetChallengeDialog(false)}
        PaperProps={{ sx: { bgcolor: '#1e222d', color: '#fff', minWidth: 400 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2a2e39', fontWeight: 600 }}>
          Reset Challenge to Phase 1
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedChallenge && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This will completely reset the challenge to Phase 1 with fresh start.
              </Typography>
              <Alert severity="warning" sx={{ bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', mb: 2 }}>
                <strong>Challenge:</strong> {selectedChallenge.ChallengeType} - ${selectedChallenge.ChallengeAmount?.toLocaleString()}<br />
                <strong>User:</strong> {selectedChallenge.FirstName} {selectedChallenge.LastName}<br />
                <strong>Current Phase:</strong> {selectedChallenge.CurrentPhase}<br />
                <br />
                <strong>This will:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Reset to Phase 1</li>
                  <li>Clear all stats and trades</li>
                  <li>Reset balance to starting amount</li>
                  <li>Clear failure reason</li>
                  <li>Set status to Active</li>
                </ul>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          <Button 
            onClick={() => setShowResetChallengeDialog(false)}
            disabled={processing}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetChallenge}
            disabled={processing}
            variant="contained"
            sx={{
              bgcolor: '#2196f3',
              color: '#fff',
              fontWeight: 600,
              '&:hover': { bgcolor: '#1976d2' },
              '&:disabled': { bgcolor: '#2a2e39', color: '#666' }
            }}
          >
            {processing ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Reset Challenge'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scalping Violations Dialog */}
      <Dialog 
        open={showScalpingDialog} 
        onClose={() => setShowScalpingDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#1e222d', color: '#fff' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #2a2e39', fontWeight: 600 }}>
          Scalping Violations
          {selectedChallenge && (
            <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>
              {selectedChallenge.ChallengeType} - {selectedChallenge.FirstName} {selectedChallenge.LastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {scalpingViolations.length === 0 ? (
            <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', color: '#64b5f6' }}>
              No scalping violations found for this challenge.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: '#131722' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Order ID</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Symbol</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Entry</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Exit</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: '#1e222d', color: '#888', fontWeight: 600 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scalpingViolations.map((violation) => (
                    <TableRow key={violation.OrderID} sx={{ '&:hover': { bgcolor: '#1e222d' } }}>
                      <TableCell sx={{ color: '#d1d4dc' }}>#{violation.OrderID}</TableCell>
                      <TableCell sx={{ color: '#d1d4dc' }}>{violation.InstrumentSymbol}</TableCell>
                      <TableCell sx={{ color: '#ef4444', fontWeight: 600 }}>
                        {violation.DurationSeconds}s
                      </TableCell>
                      <TableCell sx={{ color: '#d1d4dc' }}>{violation.EntryPrice}</TableCell>
                      <TableCell sx={{ color: '#d1d4dc' }}>{violation.ExitPrice}</TableCell>
                      <TableCell>
                        <Chip
                          label={violation.ScalpingReviewed ? 'Reviewed' : 'Pending'}
                          size="small"
                          color={violation.ScalpingReviewed ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        {!violation.ScalpingReviewed && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedScalpingOrder(violation.OrderID);
                              setShowScalpingDialog(false);
                              // Show notes input inline
                              const notes = prompt('Review notes (optional):');
                              handleReviewScalping(violation.OrderID, true);
                            }}
                            sx={{ color: '#4caf50', borderColor: '#4caf50' }}
                          >
                            Mark Reviewed
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          <Button 
            onClick={() => setShowScalpingDialog(false)}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Affiliate Dialog */}
      <Dialog
        open={showAddAffiliateDialog}
        onClose={() => {
          setShowAddAffiliateDialog(false);
          setAffiliateEmail('');
          setAffiliateCode('');
          setAffiliateNotes('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#131722',
            border: '1px solid #2a2e39'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #2a2e39',
          fontWeight: 600
        }}>
          Add New Affiliate
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Affiliate Email"
              value={affiliateEmail}
              onChange={(e) => setAffiliateEmail(e.target.value)}
              required
              placeholder="affiliate@example.com"
              InputLabelProps={{ sx: { color: '#787b86' } }}
              InputProps={{ sx: { color: '#d1d4dc' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#2a2e39' },
                  '&:hover fieldset': { borderColor: '#9333ea' },
                  '&.Mui-focused fieldset': { borderColor: '#9333ea' }
                }
              }}
            />

            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label="Referral Code"
                  value={affiliateCode}
                  InputProps={{ 
                    readOnly: true,
                    sx: { 
                      color: '#d1d4dc',
                      fontFamily: 'monospace',
                      fontWeight: 600
                    }
                  }}
                  InputLabelProps={{ sx: { color: '#787b86' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#2a2e39' },
                      bgcolor: '#1e222d'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateCode}
                  disabled={generatingCode}
                  sx={{
                    bgcolor: '#9333ea',
                    color: '#fff',
                    minWidth: '150px',
                    '&:hover': { bgcolor: '#7e22ce' },
                    '&:disabled': { bgcolor: '#666' }
                  }}
                >
                  {generatingCode ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Generate Code'}
                </Button>
              </Box>
              <Typography sx={{ color: '#666', fontSize: 12 }}>
                Click "Generate Code" to create a unique referral code
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Notes (Optional)"
              value={affiliateNotes}
              onChange={(e) => setAffiliateNotes(e.target.value)}
              multiline
              rows={3}
              placeholder="Add any notes about this affiliate..."
              InputLabelProps={{ sx: { color: '#787b86' } }}
              InputProps={{ sx: { color: '#d1d4dc' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#2a2e39' },
                  '&:hover fieldset': { borderColor: '#9333ea' },
                  '&.Mui-focused fieldset': { borderColor: '#9333ea' }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          <Button 
            onClick={() => {
              setShowAddAffiliateDialog(false);
              setAffiliateEmail('');
              setAffiliateCode('');
              setAffiliateNotes('');
            }}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateAffiliate}
            disabled={!affiliateEmail || !affiliateCode}
            sx={{
              bgcolor: '#9333ea',
              color: '#fff',
              '&:hover': { bgcolor: '#7e22ce' },
              '&:disabled': { bgcolor: '#666', color: '#999' }
            }}
          >
            Create Affiliate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Affiliate Details Dialog */}
      <Dialog
        open={showAffiliateDetailsDialog}
        onClose={() => {
          setShowAffiliateDetailsDialog(false);
          setSelectedAffiliate(null);
          setAffiliateUsers([]);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#131722',
            border: '1px solid #2a2e39'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#fff', 
          borderBottom: '1px solid #2a2e39',
          fontWeight: 600
        }}>
          Affiliate Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedAffiliate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Affiliate Info */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Email</Typography>
                  <Typography sx={{ color: '#fff', fontSize: 16 }}>
                    {selectedAffiliate.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Referral Code</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={selectedAffiliate.referralCode}
                      sx={{
                        bgcolor: '#9333ea20',
                        color: '#9333ea',
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: 16
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedAffiliate.referralCode);
                        setSuccessMessage('Referral code copied!');
                      }}
                      sx={{ color: '#666', '&:hover': { color: '#9333ea' } }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Status</Typography>
                    <Chip
                      label={selectedAffiliate.isActive ? 'Active' : 'Disabled'}
                      size="small"
                      color={selectedAffiliate.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Expiry Date</Typography>
                    {selectedAffiliate.expiryDate ? (
                      <Box>
                        <Typography sx={{ 
                          color: selectedAffiliate.isExpired ? '#ef4444' : '#fff', 
                          fontSize: 16, 
                          fontWeight: 600 
                        }}>
                          {new Date(selectedAffiliate.expiryDate).toLocaleDateString()}
                        </Typography>
                        {selectedAffiliate.isExpired && (
                          <Chip
                            label="Expired"
                            size="small"
                            sx={{ 
                              bgcolor: '#ef444420',
                              color: '#ef4444',
                              mt: 0.5
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#666', fontSize: 14 }}>
                        No Expiry
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Total Signups</Typography>
                    <Typography sx={{ color: '#fff', fontSize: 20, fontWeight: 600 }}>
                      {selectedAffiliate.totalSignups}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Active Users</Typography>
                    <Typography sx={{ color: '#4ade80', fontSize: 20, fontWeight: 600 }}>
                      {selectedAffiliate.activeUsers}
                    </Typography>
                  </Box>
                </Box>
                {selectedAffiliate.notes && (
                  <Box>
                    <Typography sx={{ color: '#888', fontSize: 12, mb: 0.5 }}>Notes</Typography>
                    <Typography sx={{ color: '#d1d4dc', fontSize: 14 }}>
                      {selectedAffiliate.notes}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Users List */}
              <Box>
                <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600, mb: 2 }}>
                  Referred Users ({affiliateUsers.length})
                </Typography>
                {affiliateUsers.length === 0 ? (
                  <Typography sx={{ color: '#666', textAlign: 'center', py: 2 }}>
                    No users have signed up with this referral code yet
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ bgcolor: '#1e222d', border: '1px solid #2a2e39' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#131722' }}>
                          <TableCell sx={{ color: '#888', fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ color: '#888', fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ color: '#888', fontWeight: 600 }}>Signup Date</TableCell>
                          <TableCell sx={{ color: '#888', fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {affiliateUsers.map((user, index) => (
                          <TableRow key={index} sx={{ '&:hover': { bgcolor: '#2a2e39' } }}>
                            <TableCell sx={{ color: '#d1d4dc' }}>{user.name}</TableCell>
                            <TableCell sx={{ color: '#d1d4dc' }}>{user.email}</TableCell>
                            <TableCell sx={{ color: '#888', fontSize: 13 }}>
                              {new Date(user.signupDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                color={user.isActive ? 'success' : 'default'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #2a2e39' }}>
          {selectedAffiliate && (
            <Button
              variant="contained"
              startIcon={<UpdateIcon />}
              onClick={() => {
                handleExtendExpiry(selectedAffiliate.affiliateId, 3);
              }}
              sx={{
                bgcolor: '#f59e0b',
                color: '#fff',
                mr: 'auto',
                '&:hover': { bgcolor: '#d97706' }
              }}
            >
              Extend Expiry (3 months)
            </Button>
          )}
          <Button 
            onClick={() => {
              setShowAffiliateDetailsDialog(false);
              setSelectedAffiliate(null);
              setAffiliateUsers([]);
            }}
            sx={{ color: '#787b86', '&:hover': { bgcolor: '#2a2e39' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* MT5 Manual Data Entry Dialog */}
      <Dialog 
        open={showMT5DataDialog} 
        onClose={() => setShowMT5DataDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#131722',
            border: '1px solid #2a2e39'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#1e222d', color: '#fff', borderBottom: '1px solid #2a2e39' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EditIcon sx={{ color: '#f59e0b' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                MT5 Manual Data Entry
              </Typography>
              {mt5SelectedUser && (
                <Typography variant="caption" sx={{ color: '#888' }}>
                  {mt5SelectedUser.FirstName} {mt5SelectedUser.LastName} ({mt5SelectedUser.EmailID})
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#131722', py: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Challenge Selector (for MT5 Credentials) */}
            {mt5UserChallenges.length > 0 && (
              <>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#787b86' }}>Select Challenge (Optional - for credentials)</InputLabel>
                    <Select
                      value={selectedChallengeId}
                      onChange={(e) => {
                        const challengeId = e.target.value;
                        setSelectedChallengeId(challengeId);
                        // Pre-fill credentials if challenge has them
                        const challenge = mt5UserChallenges.find(c => c.ChallengeID === challengeId);
                        if (challenge) {
                          setMT5Username(challenge.MT5Username || '');
                          setMT5Password(challenge.MT5Password || '');
                        }
                      }}
                      label="Select Challenge (Optional - for credentials)"
                      sx={{
                        color: '#fff',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4ade80' },
                        '.MuiSvgIcon-root': { color: '#888' }
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {mt5UserChallenges.map((challenge) => (
                        <MenuItem key={challenge.ChallengeID} value={challenge.ChallengeID}>
                          {challenge.CustomName || challenge.ChallengeType} - ${challenge.ChallengeAmount?.toLocaleString()} 
                          {challenge.CurrentPhase && ` (Phase ${challenge.CurrentPhase})`}
                          {challenge.MT5Username && ' ✓ Has Credentials'}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* MT5 Credentials (only if challenge is selected) */}
                {selectedChallengeId && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="MT5 Username"
                        fullWidth
                        value={mt5Username}
                        onChange={(e) => setMT5Username(e.target.value)}
                        InputLabelProps={{ sx: { color: '#787b86' } }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': { borderColor: '#2a2e39' },
                            '&:hover fieldset': { borderColor: '#4ade80' },
                            '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="MT5 Password"
                        fullWidth
                        value={mt5Password}
                        onChange={(e) => setMT5Password(e.target.value)}
                        InputLabelProps={{ sx: { color: '#787b86' } }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': { borderColor: '#2a2e39' },
                            '&:hover fieldset': { borderColor: '#4ade80' },
                            '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Divider */}
                <Grid item xs={12}>
                  <Box sx={{ borderBottom: '1px solid #2a2e39', my: 1 }} />
                  <Typography variant="caption" sx={{ color: '#888' }}>
                    MT5 Platform Data
                  </Typography>
                </Grid>
              </>
            )}

            {/* Initial Equity */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Initial Equity"
                type="number"
                fullWidth
                value={mt5Data.initialEquity}
                onChange={(e) => setMT5Data({ ...mt5Data, initialEquity: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#4ade80' },
                    '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                  }
                }}
              />
            </Grid>

            {/* Current Equity */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Current Equity"
                type="number"
                fullWidth
                value={mt5Data.currentEquity}
                onChange={(e) => setMT5Data({ ...mt5Data, currentEquity: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#4ade80' },
                    '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                  }
                }}
              />
            </Grid>

            {/* Profit */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Profit"
                type="number"
                fullWidth
                value={mt5Data.profit}
                onChange={(e) => setMT5Data({ ...mt5Data, profit: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#4ade80' },
                    '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                  }
                }}
              />
            </Grid>

            {/* Profit Target Percentage (Auto-calculated) */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Profit Target %"
                fullWidth
                value={
                  mt5Data.initialEquity && mt5Data.profit
                    ? ((parseFloat(mt5Data.profit) / parseFloat(mt5Data.initialEquity)) * 100).toFixed(2)
                    : '0.00'
                }
                InputLabelProps={{ sx: { color: '#787b86' } }}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#4ade80',
                    bgcolor: 'rgba(74, 222, 128, 0.05)',
                    '& fieldset': { borderColor: '#2a2e39' }
                  }
                }}
              />
            </Grid>

            {/* Number of Orders Placed */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Orders Placed"
                type="number"
                fullWidth
                value={mt5Data.ordersPlaced}
                onChange={(e) => setMT5Data({ ...mt5Data, ordersPlaced: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#4ade80' },
                    '&.Mui-focused fieldset': { borderColor: '#4ade80' }
                  }
                }}
              />
            </Grid>

            {/* Daily Drawdown % */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Daily Drawdown %"
                type="number"
                fullWidth
                value={mt5Data.dailyDrawdown}
                onChange={(e) => setMT5Data({ ...mt5Data, dailyDrawdown: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                  }
                }}
              />
            </Grid>

            {/* Total Drawdown % */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Drawdown %"
                type="number"
                fullWidth
                value={mt5Data.totalDrawdown}
                onChange={(e) => setMT5Data({ ...mt5Data, totalDrawdown: e.target.value })}
                InputLabelProps={{ sx: { color: '#787b86' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#2a2e39' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                  }
                }}
              />
            </Grid>

            {/* Has Violations */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#787b86' }}>Has Violations?</InputLabel>
                <Select
                  value={mt5Data.hasViolations}
                  onChange={(e) => setMT5Data({ ...mt5Data, hasViolations: e.target.value })}
                  label="Has Violations?"
                  sx={{
                    color: '#fff',
                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#2a2e39' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ef4444' },
                    '.MuiSvgIcon-root': { color: '#888' }
                  }}
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Violation Reason (conditional) */}
            {mt5Data.hasViolations === 'Yes' && (
              <Grid item xs={12}>
                <TextField
                  label="Reason for Violations"
                  fullWidth
                  multiline
                  rows={3}
                  value={mt5Data.violationReason}
                  onChange={(e) => setMT5Data({ ...mt5Data, violationReason: e.target.value })}
                  InputLabelProps={{ sx: { color: '#787b86' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#2a2e39' },
                      '&:hover fieldset': { borderColor: '#ef4444' },
                      '&.Mui-focused fieldset': { borderColor: '#ef4444' }
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1e222d', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowMT5DataDialog(false);
              setMT5SelectedUser(null);
              setSelectedChallengeId('');
              setMT5Username('');
              setMT5Password('');
              setMT5UserChallenges([]);
            }}
            sx={{ color: '#888' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitMT5Data}
            disabled={
              processing ||
              !mt5Data.initialEquity || 
              !mt5Data.currentEquity || 
              !mt5Data.profit || 
              !mt5Data.ordersPlaced ||
              !mt5Data.dailyDrawdown ||
              !mt5Data.totalDrawdown ||
              (mt5Data.hasViolations === 'Yes' && !mt5Data.violationReason)
            }
            sx={{
              bgcolor: '#4ade80',
              color: '#1e293b',
              fontWeight: 700,
              '&:hover': {
                bgcolor: '#22c55e'
              },
              '&.Mui-disabled': {
                bgcolor: '#2a2e39',
                color: '#666'
              }
            }}
          >
            {processing ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MT5 Data History Dialog */}
      <Dialog 
        open={showMT5HistoryDialog} 
        onClose={() => {
          setShowMT5HistoryDialog(false);
          setMT5HistoryUser(null);
          setMT5History([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#1e222d',
            color: '#e2e8f0',
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#2a2e39', 
          color: '#e2e8f0', 
          borderBottom: '1px solid #3a3e49',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <ShowChartIcon sx={{ color: '#10b981' }} />
          MT5 Data History - {mt5HistoryUser?.EmailID}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#1e222d', mt: 2 }}>
          {loadingMT5History ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#10b981' }} />
            </Box>
          ) : mt5History.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#888' }}>No MT5 data entries found for this user.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Entry Date</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Admin Email</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Initial Equity</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Current Equity</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Profit</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Profit %</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Orders</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Daily DD%</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Total DD%</TableCell>
                    <TableCell sx={{ color: '#888', fontWeight: 600, borderBottom: '1px solid #2a2e39' }}>Violations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mt5History.map((entry, index) => (
                    <TableRow key={entry.EntryID || index}>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        {new Date(entry.EntryDate).toLocaleString()}
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        {entry.AdminEmail}
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        ${parseFloat(entry.InitialEquity).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        ${parseFloat(entry.CurrentEquity).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ 
                        color: parseFloat(entry.Profit) >= 0 ? '#4ade80' : '#f87171',
                        borderBottom: '1px solid #2a2e39',
                        fontWeight: 600
                      }}>
                        ${parseFloat(entry.Profit).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ 
                        color: parseFloat(entry.ProfitTargetPercent) >= 0 ? '#4ade80' : '#f87171',
                        borderBottom: '1px solid #2a2e39',
                        fontWeight: 600
                      }}>
                        {parseFloat(entry.ProfitTargetPercent).toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        {entry.OrdersPlaced}
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        {parseFloat(entry.DailyDrawdownPercent).toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ color: '#e2e8f0', borderBottom: '1px solid #2a2e39' }}>
                        {parseFloat(entry.TotalDrawdownPercent).toFixed(2)}%
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #2a2e39' }}>
                        {entry.HasViolations ? (
                          <Box>
                            <Typography sx={{ color: '#f87171', fontWeight: 600, fontSize: '0.875rem' }}>
                              Yes
                            </Typography>
                            {entry.ViolationReason && (
                              <Typography sx={{ color: '#888', fontSize: '0.75rem', mt: 0.5 }}>
                                {entry.ViolationReason}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography sx={{ color: '#4ade80', fontWeight: 600, fontSize: '0.875rem' }}>
                            No
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1e222d', borderTop: '1px solid #2a2e39', px: 3, py: 2 }}>
          <Button 
            onClick={() => {
              setShowMT5HistoryDialog(false);
              setMT5HistoryUser(null);
              setMT5History([]);
            }}
            sx={{ color: '#888' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;

